import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MapContainer,
  Marker,
  TileLayer,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import { LocateMeControl } from '../../../components/maps/LocateMeControl';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  ArrowRight,
  BookOpen,
  Check,
  CheckCircle,
  ChevronDown,
  Clock,
  ClipboardCheck,
  Cloud,
  FileText,
  FolderOpen,
  Heart,
  Loader2,
  LocateFixed,
  Mail,
  MailX,
  MapPin,
  Navigation,
  Pencil,
  Phone,
  Plus,
  Trash2,
  User,
  X,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { parseDateOnly } from '../../../utils/date';
import { sitesService } from '../../../services/sitesService';
import type { ActionRequired } from '../../../services/sitesService';
import { api } from '../../../services/api';
import { StatusBadge, PriorityBadge } from '../../../components/ui/StatusBadge';

import { QueryErrorBanner } from '../../../components/ui/query-error-banner';
import { formatPhone } from '../../../utils/formatPhone';
import { usePermissions } from '../../../hooks/usePermissions';
import { track } from '../../../services/telemetry';
import { useToast } from '@/hooks/useToast';
import type { ContactResponse } from '../../../types/api';
import { groupContactsByRole } from './groupContactsByRole';
import { ContactEditModal } from '../../sites/components/ContactEditModal';

const CONTACT_TOOL_MODAL = { type: 'site_contact' as const };
import { ConfirmProvider } from '../../../components/ui/confirm-dialog';
import { WeatherWidget } from './WeatherWidget';
import { LiteSiteRecords } from './LiteSiteRecords';
import { LiteSiteDocuments } from './LiteSiteDocuments';
import { WeatherHistoryCalendar } from './WeatherHistoryCalendar';
import { openDatePicker } from '@/utils/datePicker';

// Match the Leaflet icon fix used by the rest of the lite map surfaces.
delete (L.Icon.Default.prototype as unknown as { _getIconUrl?: unknown })
  ._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl:
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Recenters the edit map when the draft coordinates change (e.g. after "Use my
// location"), since MapContainer only honors `center` on mount.
function RecenterMap({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();
  map.setView([lat, lng]);
  return null;
}

// Tap anywhere on the map to drop the pin there. Dragging a small marker on a
// phone touchscreen is fiddly (Jim); a tap is far easier. Drag still works for
// fine-tuning on a desktop/trackpad.
function TapToSetPin({
  onPick,
}: {
  onPick: (lat: number, lng: number) => void;
}) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

// Mobile-scaled mirror of the hub SiteDetailContent CollapsibleSection.
// Children lazy-mount, so a collapsed section's queries never fire.
function LiteCollapsibleSection({
  id,
  title,
  icon: Icon,
  badge,
  open,
  onToggle,
  headerAction,
  children,
}: {
  id: string;
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: string;
  open: boolean;
  onToggle: () => void;
  headerAction?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section
      id={`lite-site-section-${id}`}
      className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 scroll-mt-20"
    >
      <div className="w-full px-4 py-3 flex items-center gap-2">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          className="flex-1 min-w-0 flex items-center gap-2 text-left"
        >
          <Icon className="w-5 h-5 text-primary-600 dark:text-primary-400 shrink-0" />
          <h2 className="text-base font-semibold text-slate-900 dark:text-white truncate">
            {title}
          </h2>
          {badge && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 shrink-0">
              {badge}
            </span>
          )}
        </button>
        {headerAction}
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-label={open ? `Collapse ${title}` : `Expand ${title}`}
          className="p-1 shrink-0"
        >
          <ChevronDown
            className={`w-4 h-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
          />
        </button>
      </div>
      {open && (
        <div className="border-t border-slate-100 dark:border-slate-700">
          {children}
        </div>
      )}
    </section>
  );
}

// Jump pills mirroring the hub SectionNav — expand + scroll to a section.
function LiteSectionNav({
  sections,
  onSelect,
}: {
  sections: Array<{ id: string; label: string }>;
  onSelect: (id: string) => void;
}) {
  return (
    <nav className="flex flex-wrap gap-2" aria-label="Site sections">
      {sections.map((s) => (
        <button
          key={s.id}
          type="button"
          onClick={() => onSelect(s.id)}
          className="px-3 py-1.5 text-xs font-medium rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-primary-100 hover:text-primary-700 dark:hover:bg-primary-900/30 dark:hover:text-primary-300 transition-colors"
        >
          {s.label}
        </button>
      ))}
    </nav>
  );
}

const SECTIONS = [
  { id: 'map', label: 'Map' },
  { id: 'contacts', label: 'Contacts' },
  { id: 'journal', label: 'Journal' },
  { id: 'records', label: 'Inspections' },
  { id: 'documents', label: 'Documents' },
  { id: 'weather', label: 'Weather' },
  { id: 'actions', label: 'Open Actions' },
];

const DEFAULT_OPEN_SECTIONS: Record<string, boolean> = {
  map: true,
  contacts: true,
  actions: true,
  journal: false,
  records: false,
  documents: false,
  weather: false,
};

function ContactRow({
  contact,
  canEdit,
  onEdit,
}: {
  contact: ContactResponse;
  canEdit: boolean;
  onEdit: () => void;
}) {
  return (
    <li className="px-4 py-2.5">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
              {contact.first_name} {contact.last_name}
            </p>
            {contact.notify_email ? (
              <Mail
                className="w-3.5 h-3.5 text-green-600 dark:text-green-400 shrink-0"
                aria-label="Receives inspection report emails"
              />
            ) : (
              <MailX
                className="w-3.5 h-3.5 text-slate-400 shrink-0"
                aria-label="Muted — not on email distribution"
              />
            )}
          </div>
          <p className="text-xs text-slate-500 dark:text-slate-400 capitalize">
            {(contact.role ?? 'contact').replace(/_/g, ' ')}
            {contact.company ? ` · ${contact.company}` : ''}
          </p>
        </div>
        {canEdit && contact.id && (
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Staff records are edited in People Hub; the row can still be
                removed from the site or have its email switched. */}
            {!contact.is_staff && (
              <button
                type="button"
                onClick={onEdit}
                className="p-1 text-slate-400 hover:text-primary-600 dark:hover:text-primary-400 rounded-md"
                title="Edit contact"
              >
                <Pencil className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
      </div>
      <div className="mt-1.5 space-y-0.5">
        {contact.email && (
          <a
            href={`mailto:${contact.email}`}
            className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-400"
          >
            <Mail className="w-3 h-3 flex-shrink-0" />
            <span className="truncate">{contact.email}</span>
          </a>
        )}
        {contact.phone && (
          <a
            href={`tel:${contact.phone}`}
            onClick={() =>
              track('contact_called', { role: contact.role ?? 'contact' })
            }
            className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-400"
          >
            <Phone className="w-3 h-3 flex-shrink-0" />
            <span>{formatPhone(contact.phone)}</span>
          </a>
        )}
      </div>
    </li>
  );
}

// Open corrective actions only — the ActionRequired query the Lite flow
// already uses, scoped to open items. Full history stays on the hub.
function OpenActionsSection({ siteId }: { siteId: string }) {
  const {
    data: actionsData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['site-open-actions', siteId],
    queryFn: () => sitesService.getSiteActionsRequired(siteId, 50, false),
    enabled: !!siteId,
    retry: 1,
    refetchOnMount: 'always',
    staleTime: 0,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="p-4">
        <QueryErrorBanner error={error} onRetry={refetch} compact />
      </div>
    );
  }

  const openActions = (actionsData?.actions ?? []).filter(
    (a: ActionRequired) => a.status === 'open'
  );
  if (openActions.length === 0) {
    return (
      <div className="px-4 py-6 text-center">
        <CheckCircle className="w-8 h-8 text-success-500 mx-auto mb-2" />
        <p className="text-sm text-slate-500 dark:text-slate-400">
          No open corrective actions
        </p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-slate-100 dark:divide-slate-700 max-h-80 overflow-y-auto">
      {openActions.map((action, idx) => (
        <div key={action.id} className="px-4 py-3">
          <div className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-700 text-xs font-semibold text-slate-500 dark:text-slate-400 flex items-center justify-center mt-0.5">
              {idx + 1}
            </span>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <p className="text-sm font-medium text-slate-900 dark:text-white leading-snug">
                  {action.corrective_action || action.item}
                </p>
                <StatusBadge variant="warning">Open</StatusBadge>
              </div>
              {action.corrective_action && (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  {action.item}
                </p>
              )}
              {action.location && (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                  📍 {action.location}
                </p>
              )}
              <div className="flex items-center justify-between mt-1.5">
                <PriorityBadge priority={action.priority} />
                <p className="text-xs text-slate-400 dark:text-slate-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {action.due_text}
                </p>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

type JournalNote = {
  id: string;
  content: string;
  category?: string | null;
  is_important: boolean;
  created_at: string;
};

function JournalSection({
  siteId,
  canAddJournal,
}: {
  siteId: string;
  canAddJournal: boolean;
}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [newNote, setNewNote] = useState('');

  const {
    data: journalData,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['site-journal', siteId],
    queryFn: async () => {
      const response = await api.get<{ notes: JournalNote[] }>(
        `/sites/${siteId}/journal`
      );
      return response.data;
    },
    enabled: !!siteId,
    retry: 1,
  });

  const addNoteMutation = useMutation({
    mutationFn: (content: string) =>
      api.post(`/sites/${siteId}/journal`, {
        content,
        category: null,
        is_important: false,
      }),
    onSuccess: () => {
      track('journal_note_added', { site_id: siteId });
      queryClient.invalidateQueries({ queryKey: ['site-journal', siteId] });
      setNewNote('');
    },
    onError: () => {
      toast({
        title: 'Save Failed',
        description: 'Failed to save note. Please try again.',
        variant: 'destructive',
      });
    },
  });

  const toggleImportantMutation = useMutation({
    mutationFn: ({
      noteId,
      isImportant,
    }: {
      noteId: string;
      isImportant: boolean;
    }) =>
      api.patch(`/sites/${siteId}/journal/${noteId}`, {
        is_important: isImportant,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-journal', siteId] });
    },
  });

  const deleteNoteMutation = useMutation({
    mutationFn: (noteId: string) =>
      api.delete(`/sites/${siteId}/journal/${noteId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site-journal', siteId] });
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
      </div>
    );
  }
  if (isError) {
    return (
      <div className="p-4">
        <QueryErrorBanner error={error} onRetry={refetch} compact />
      </div>
    );
  }

  const sortedNotes = [...(journalData?.notes ?? [])].sort((a, b) => {
    if (a.is_important && !b.is_important) return -1;
    if (!a.is_important && b.is_important) return 1;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  return (
    <div>
      {canAddJournal && (
        <div className="px-4 py-2.5 border-b border-slate-100 dark:border-slate-700">
          <div className="flex items-end gap-2">
            <textarea
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              rows={2}
              placeholder="Leave a note about this site..."
              className="flex-1 text-sm px-2.5 py-1.5 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-primary-500 resize-y"
            />
            <button
              type="button"
              onClick={() => newNote.trim() && addNoteMutation.mutate(newNote)}
              disabled={!newNote.trim() || addNoteMutation.isPending}
              className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 whitespace-nowrap flex items-center gap-1.5"
            >
              {addNoteMutation.isPending && (
                <Loader2 className="w-3 h-3 animate-spin" />
              )}
              Add
            </button>
          </div>
        </div>
      )}
      {sortedNotes.length === 0 ? (
        <p className="px-4 py-4 text-sm text-slate-500 dark:text-slate-400 italic">
          No notes yet
        </p>
      ) : (
        <div className="divide-y divide-slate-100 dark:divide-slate-700 max-h-64 overflow-y-auto">
          {sortedNotes.map((note) => (
            <div
              key={note.id}
              className={`px-4 py-3 ${note.is_important ? 'bg-pink-50/50 dark:bg-pink-900/10' : ''}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  {note.category && note.category !== 'Custom Note' && (
                    <p className="text-xs font-medium text-primary-600 dark:text-primary-400 mb-1">
                      {note.category}
                    </p>
                  )}
                  <p className="text-sm text-slate-900 dark:text-white whitespace-pre-wrap">
                    {note.content}
                  </p>
                  <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">
                    {format(parseISO(note.created_at), 'MMM d, yyyy')}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() =>
                      toggleImportantMutation.mutate({
                        noteId: note.id,
                        isImportant: !note.is_important,
                      })
                    }
                    className={`p-1.5 rounded-lg transition-colors ${
                      note.is_important
                        ? 'bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400'
                        : 'text-slate-400 hover:text-pink-500 hover:bg-slate-100 dark:hover:bg-slate-700'
                    }`}
                  >
                    <Heart
                      className={`w-4 h-4 ${note.is_important ? 'fill-current' : ''}`}
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm('Delete this note?')) {
                        deleteNoteMutation.mutate(note.id);
                      }
                    }}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-danger-500 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

type LiteSiteDetailContentProps = {
  siteId: string;
  /**
   * When provided, the component renders in drawer mode (inspection flow):
   * shows a close affordance and a link out to the full browse page. Content
   * and capabilities are identical in both modes (EP-LITEPARITY).
   */
  onClose?: () => void;
};

export function LiteSiteDetailContent({
  siteId,
  onClose,
}: LiteSiteDetailContentProps) {
  // Usage telemetry: how the inspector reached the site detail — the browse
  // page (/lite/sites/:id) or the inspection-flow drawer (onClose present).
  const via = onClose ? 'drawer' : 'browse';
  useEffect(() => {
    if (!siteId) return;
    track('lite_site_opened', { site_id: siteId, via });
  }, [siteId, via]);

  const queryClient = useQueryClient();
  const { canAny, isInspector } = usePermissions();
  // Inspector capabilities (mostly-see, sometimes-edit): the backend endpoints
  // are the real gates; these only hide affordances from clients/viewers.
  const canEditLocation =
    canAny('sites:update_location', 'sites:update') || isInspector;
  const canEditDetails =
    canAny('sites:update_details', 'sites:update') || isInspector;
  const canEditContacts =
    canAny('sites:contacts', 'sites:update') || isInspector;
  const canAddJournal = canAny('sites:journal', 'sites:update') || isInspector;
  // Jim 2026-08-29: inspectors keep the consultant up to date from the field.
  const canChangeConsultant = canAny('sites:update') || isInspector;
  const [consultantPickerOpen, setConsultantPickerOpen] = useState(false);

  const [openSections, setOpenSections] = useState<Record<string, boolean>>(
    DEFAULT_OPEN_SECTIONS
  );
  const toggleSection = (id: string) =>
    setOpenSections((prev) => ({ ...prev, [id]: !prev[id] }));
  const jumpToSection = (id: string) => {
    setOpenSections((prev) => ({ ...prev, [id]: true }));
    requestAnimationFrame(() => {
      document
        .getElementById(`lite-site-section-${id}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  const {
    data: site,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['site', siteId],
    queryFn: () => sitesService.getSite(siteId),
    enabled: !!siteId,
    retry: 1,
  });
  // The unified contact list (site_user_links + users) — the rows the report
  // emails read. `site.contacts` is the legacy table: editing a person there
  // never reached the recipient (Jim 10/21, "edit is needed especially for
  // an inspector").
  const { data: siteContacts = [], isSuccess: contactsLoaded } = useQuery({
    queryKey: ['site-contacts', siteId],
    queryFn: () => sitesService.getContacts(siteId),
    enabled: !!siteId,
    retry: 1,
  });
  // The flow's recipients check links here with #contacts. Wait for the site
  // too — the list can land before the page body exists (gate 10/22, the
  // section sat 925 px down) — and scroll after that render has painted.
  useEffect(() => {
    if (!contactsLoaded || isLoading || window.location.hash !== '#contacts')
      return;
    const frame = requestAnimationFrame(() =>
      document
        .getElementById('lite-site-section-contacts')
        ?.scrollIntoView({ block: 'start' })
    );
    return () => cancelAnimationFrame(frame);
  }, [contactsLoaded, isLoading]);

  // --- Permit / estimated-completion tap-to-edit (inspector-safe endpoint) ---
  const [editingField, setEditingField] = useState<
    'permit' | 'completion' | null
  >(null);
  const [detailsDraft, setDetailsDraft] = useState({
    permit_number: '',
    permit_expiration_date: '',
    estimated_end_date: '',
  });
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const saveDetails = useMutation({
    mutationFn: () =>
      sitesService.updateSiteDetails(siteId, {
        permit_number: detailsDraft.permit_number.trim() || null,
        permit_expiration_date: detailsDraft.permit_expiration_date || null,
        estimated_end_date: detailsDraft.estimated_end_date || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site', siteId] });
      setEditingField(null);
      setDetailsError(null);
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      setDetailsError(detail || 'Could not save. Please try again.');
    },
  });
  const startEditField = (field: 'permit' | 'completion') => {
    setDetailsDraft({
      permit_number: site?.permit_number ?? '',
      permit_expiration_date: site?.permit_expiration_date?.slice(0, 10) ?? '',
      estimated_end_date: site?.estimated_end_date?.slice(0, 10) ?? '',
    });
    setDetailsError(null);
    setEditingField(field);
  };

  // --- Location editing: pin only (tap/drag/geolocate). Jim: "they can use
  // the map" — deliberately NO lat/lng or address text inputs. ---
  const [editingLocation, setEditingLocation] = useState(false);
  const [pinDraft, setPinDraft] = useState<{
    latitude: number | null;
    longitude: number | null;
  }>({ latitude: null, longitude: null });
  const [locError, setLocError] = useState<string | null>(null);
  const saveLocation = useMutation({
    mutationFn: () =>
      sitesService.updateSiteLocation(siteId, {
        coordinates:
          pinDraft.latitude != null && pinDraft.longitude != null
            ? { latitude: pinDraft.latitude, longitude: pinDraft.longitude }
            : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site', siteId] });
      setEditingLocation(false);
      setLocError(null);
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      setLocError(detail || 'Failed to save location. Please try again.');
    },
  });
  const startEditLocation = () => {
    setPinDraft({
      latitude: site?.coordinates?.latitude ?? null,
      longitude: site?.coordinates?.longitude ?? null,
    });
    setLocError(null);
    setEditingLocation(true);
  };
  const useMyLocation = () => {
    if (!navigator.geolocation) {
      setLocError('Location is not available on this device.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) =>
        setPinDraft({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        }),
      () => setLocError('Could not get your location (permission denied?).'),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // The Hub's contact tool, opened here (JIM-1023-01, Jim 10/22: "use the same
  // tool we are using in sites on the Hub"): pick list, People Hub, new contact,
  // and the current list with its switches and edit.
  const [contactTool, setContactTool] = useState<'pick' | 'view' | null>(null);

  const { data: consultantOptions = [] } = useQuery({
    queryKey: ['site-consultant-options', siteId],
    queryFn: () => sitesService.getConsultantOptions(siteId),
    enabled: consultantPickerOpen,
    staleTime: 5 * 60 * 1000,
  });
  const assignConsultantMutation = useMutation({
    mutationFn: (consultantId: string | null) =>
      sitesService.assignConsultant(siteId, consultantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['site', siteId] });
      setConsultantPickerOpen(false);
    },
  });

  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        <div className="animate-pulse rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 space-y-3">
          <div className="h-5 w-2/3 rounded-md bg-slate-200 dark:bg-slate-700" />
          <div className="h-3 w-1/2 rounded-md bg-slate-200 dark:bg-slate-700" />
        </div>
        <div className="animate-pulse h-48 rounded-xl bg-slate-200 dark:bg-slate-700" />
      </div>
    );
  }

  if (isError || !site) {
    return (
      <div className="p-4">
        <QueryErrorBanner
          error={error ?? new Error('Site not found')}
          onRetry={refetch}
          message={!isError ? 'Site not found' : undefined}
        />
      </div>
    );
  }

  const grouped = groupContactsByRole(siteContacts);
  const contactGroups: Array<{
    label: string;
    contacts: ContactResponse[];
    fallback?: React.ReactNode;
  }> = [
    {
      label: 'Consultant',
      contacts: grouped.consultants,
      fallback: site.assigned_consultant_name ? (
        <p className="px-4 py-2 text-sm text-slate-700 dark:text-slate-300">
          {site.assigned_consultant_name}
        </p>
      ) : undefined,
    },
    {
      label: 'Inspector',
      contacts: grouped.inspectors,
      fallback: site.assigned_inspector_name ? (
        <div className="px-4 py-2">
          <p className="text-sm text-slate-700 dark:text-slate-300">
            {site.assigned_inspector_name}
          </p>
          {site.assigned_inspector_email && (
            <a
              href={`mailto:${site.assigned_inspector_email}`}
              className="flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-400 mt-0.5"
            >
              <Mail className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{site.assigned_inspector_email}</span>
            </a>
          )}
        </div>
      ) : undefined,
    },
    { label: 'Site Contacts', contacts: grouped.siteContacts },
  ];

  return (
    <div className="space-y-4">
      {/* Overview card */}
      <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">
              {site.name}
            </h1>
            {site.client_name && (
              <p className="text-sm text-slate-600 dark:text-slate-400 mt-0.5">
                {site.client_name}
              </p>
            )}
          </div>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="p-2 -mt-1 -mr-1 rounded-lg text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors flex-shrink-0"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="mt-2">
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {site.address?.street1}
          </p>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {site.address?.city}, {site.address?.state} {site.address?.zip_code}
          </p>
        </div>

        <div className="flex items-center gap-2 mt-2 flex-wrap">
          <StatusBadge
            variant={
              site.status === 'active'
                ? 'success'
                : site.status === 'pending'
                  ? 'warning'
                  : 'neutral'
            }
            icon="auto"
            size="sm"
          >
            {site.status.charAt(0).toUpperCase() + site.status.slice(1)}
          </StatusBadge>
          {site.project_type && (
            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 capitalize">
              {site.project_type}
            </span>
          )}
          {site.project_id && (
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {site.project_id}
            </span>
          )}
        </div>

        {/* Tap-to-edit permit + completion tiles */}
        {editingField ? (
          <div className="mt-4 space-y-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3">
            {editingField === 'permit' ? (
              <>
                <label className="block">
                  <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
                    Permit number
                  </span>
                  <input
                    type="text"
                    value={detailsDraft.permit_number}
                    onChange={(e) =>
                      setDetailsDraft((d) => ({
                        ...d,
                        permit_number: e.target.value,
                      }))
                    }
                    placeholder="e.g. LAR10XXXX"
                    className="mt-0.5 w-full text-sm px-2.5 py-1.5 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </label>
                <label className="block">
                  <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
                    Permit expiration
                  </span>
                  <input
                    type="date"
                    onClick={openDatePicker}
                    value={detailsDraft.permit_expiration_date}
                    onChange={(e) =>
                      setDetailsDraft((d) => ({
                        ...d,
                        permit_expiration_date: e.target.value,
                      }))
                    }
                    className="mt-0.5 w-full text-sm px-2.5 py-1.5 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                </label>
              </>
            ) : (
              <label className="block">
                <span className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
                  Estimated completion
                </span>
                <input
                  type="date"
                  onClick={openDatePicker}
                  value={detailsDraft.estimated_end_date}
                  onChange={(e) =>
                    setDetailsDraft((d) => ({
                      ...d,
                      estimated_end_date: e.target.value,
                    }))
                  }
                  className="mt-0.5 w-full text-sm px-2.5 py-1.5 border border-slate-200 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-200 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </label>
            )}
            {detailsError && (
              <p className="text-xs text-red-600 dark:text-red-400">
                {detailsError}
              </p>
            )}
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => {
                  setEditingField(null);
                  setDetailsError(null);
                }}
                disabled={saveDetails.isPending}
                className="px-3 py-1.5 text-xs font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => saveDetails.mutate()}
                disabled={saveDetails.isPending}
                className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 flex items-center gap-1"
              >
                {saveDetails.isPending ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Check className="w-3 h-3" />
                )}
                Save
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-3 gap-2 mt-4">
            <button
              type="button"
              onClick={() => canEditDetails && startEditField('permit')}
              disabled={!canEditDetails}
              className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3 text-left group disabled:cursor-default"
            >
              <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                Permit
                {canEditDetails && (
                  <Pencil className="w-2.5 h-2.5 text-slate-400" />
                )}
              </p>
              <p className="text-sm font-medium text-slate-900 dark:text-white truncate">
                {site.permit_number || '—'}
              </p>
            </button>
            <button
              type="button"
              onClick={() => canEditDetails && startEditField('permit')}
              disabled={!canEditDetails}
              className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3 text-left group disabled:cursor-default"
            >
              <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                Expires
                {canEditDetails && (
                  <Pencil className="w-2.5 h-2.5 text-slate-400" />
                )}
              </p>
              <p className="text-sm font-medium text-slate-900 dark:text-white">
                {site.permit_expiration_date
                  ? format(
                      parseDateOnly(site.permit_expiration_date) as Date,
                      'M/d/yy'
                    )
                  : '—'}
              </p>
            </button>
            <button
              type="button"
              onClick={() => canEditDetails && startEditField('completion')}
              disabled={!canEditDetails}
              className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-3 text-left group disabled:cursor-default"
            >
              <p className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                Est. done
                {canEditDetails && (
                  <Pencil className="w-2.5 h-2.5 text-slate-400" />
                )}
              </p>
              <p className="text-sm font-medium text-slate-900 dark:text-white">
                {site.estimated_end_date
                  ? format(
                      parseDateOnly(site.estimated_end_date) as Date,
                      'M/d/yy'
                    )
                  : '—'}
              </p>
            </button>
          </div>
        )}

        {onClose && (
          <Link
            to={`/lite/sites/${siteId}`}
            onClick={onClose}
            className="inline-flex items-center gap-1 mt-3 text-xs font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300"
          >
            Open full page
            <ArrowRight className="w-3 h-3" />
          </Link>
        )}
      </div>

      <LiteSectionNav sections={SECTIONS} onSelect={jumpToSection} />

      {/* Map / location — pin editing only */}
      <LiteCollapsibleSection
        id="map"
        title="Map"
        icon={MapPin}
        open={!!openSections.map}
        onToggle={() => toggleSection('map')}
        headerAction={
          openSections.map ? (
            editingLocation ? (
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  onClick={() => {
                    setEditingLocation(false);
                    setLocError(null);
                  }}
                  disabled={saveLocation.isPending}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 rounded-md hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50"
                >
                  <X className="w-3 h-3" />
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => saveLocation.mutate()}
                  disabled={
                    saveLocation.isPending ||
                    pinDraft.latitude == null ||
                    pinDraft.longitude == null
                  }
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
                >
                  {saveLocation.isPending ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <Check className="w-3 h-3" />
                  )}
                  Save
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                {site.coordinates && (
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${site.coordinates.latitude},${site.coordinates.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 rounded-md hover:bg-primary-100 dark:hover:bg-primary-900/30"
                  >
                    <Navigation className="w-3 h-3" />
                    Directions
                  </a>
                )}
                {canEditLocation && (
                  <button
                    type="button"
                    onClick={startEditLocation}
                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 rounded-md hover:bg-slate-200 dark:hover:bg-slate-600"
                  >
                    <Pencil className="w-3 h-3" />
                    Edit
                  </button>
                )}
              </div>
            )
          ) : undefined
        }
      >
        {editingLocation ? (
          <div className="px-4 py-3 space-y-2">
            <button
              type="button"
              onClick={useMyLocation}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-primary-700 dark:text-primary-300 bg-primary-50 dark:bg-primary-900/20 rounded-md hover:bg-primary-100 dark:hover:bg-primary-900/30"
            >
              <LocateFixed className="w-3.5 h-3.5" />
              Use my current location
            </button>
            {pinDraft.latitude != null && pinDraft.longitude != null ? (
              <>
                <p className="text-xs text-slate-400 dark:text-slate-500">
                  Tap the map to place the pin (easiest on a phone), drag it to
                  fine-tune, or tap “Use my current location”.
                </p>
                {/* Jenna 11/2: moving pins across a state needs room and the
                    wheel/pinch, not a sliver with plus and minus buttons. */}
                <div className="h-[60vh] min-h-[320px] relative z-0 rounded-md overflow-hidden border border-slate-200 dark:border-slate-700">
                  <MapContainer
                    center={[pinDraft.latitude, pinDraft.longitude]}
                    zoom={16}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom
                    touchZoom
                    doubleClickZoom
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <Marker
                      draggable
                      position={[pinDraft.latitude, pinDraft.longitude]}
                      eventHandlers={{
                        dragend: (e) => {
                          const ll = (e.target as L.Marker).getLatLng();
                          setPinDraft({ latitude: ll.lat, longitude: ll.lng });
                        },
                      }}
                    />
                    <TapToSetPin
                      onPick={(lat, lng) =>
                        setPinDraft({ latitude: lat, longitude: lng })
                      }
                    />
                    <RecenterMap
                      lat={pinDraft.latitude}
                      lng={pinDraft.longitude}
                    />
                    <LocateMeControl />
                  </MapContainer>
                </div>
              </>
            ) : (
              <p className="text-xs text-slate-400 dark:text-slate-500">
                No location set yet — tap “Use my current location” to drop the
                pin where you're standing.
              </p>
            )}
            {locError && (
              <p className="text-xs text-red-600 dark:text-red-400">
                {locError}
              </p>
            )}
          </div>
        ) : site.coordinates ? (
          <div className="h-64 relative z-0">
            <MapContainer
              center={[site.coordinates.latitude, site.coordinates.longitude]}
              zoom={15}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={false}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Marker
                position={[
                  site.coordinates.latitude,
                  site.coordinates.longitude,
                ]}
              />
              <LocateMeControl />
            </MapContainer>
          </div>
        ) : (
          <p className="px-4 py-4 text-sm text-slate-500 dark:text-slate-400 italic">
            No location set for this site.
          </p>
        )}
      </LiteCollapsibleSection>

      {/* Contacts — role-grouped, tap-to-call/email, Mail = report recipient */}
      <LiteCollapsibleSection
        id="contacts"
        title="Contacts"
        icon={User}
        badge={String(siteContacts.length)}
        open={!!openSections.contacts}
        onToggle={() => toggleSection('contacts')}
        headerAction={
          openSections.contacts && canEditContacts ? (
            <button
              type="button"
              onClick={() => setContactTool('view')}
              className="flex items-center gap-1 text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
            >
              <Pencil className="w-3.5 h-3.5" />
              Edit
            </button>
          ) : undefined
        }
      >
        {site.transition_phase !== 'live' && siteContacts.length > 0 && (
          <p className="px-4 py-2 text-xs text-slate-500 dark:text-slate-400 border-b border-slate-100 dark:border-slate-700">
            Until this site is marked live, everyone on its contact list gets
            the report, switched on or not.
          </p>
        )}
        {contactGroups.map((group) => (
          <div key={group.label}>
            <div className="flex items-center justify-between px-4 pt-3 pb-1">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">
                {group.label}
              </p>
              {group.label === 'Consultant' &&
                canChangeConsultant &&
                !consultantPickerOpen && (
                  <button
                    type="button"
                    onClick={() => setConsultantPickerOpen(true)}
                    className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    {site.assigned_consultant_name ? 'Change' : 'Assign'}
                  </button>
                )}
            </div>
            {group.label === 'Consultant' && consultantPickerOpen && (
              <div className="px-4 pb-2 flex items-center gap-2">
                <select
                  className="flex-1 text-sm rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-1.5"
                  defaultValue={site.assigned_consultant_id ?? ''}
                  disabled={assignConsultantMutation.isPending}
                  onChange={(e) =>
                    assignConsultantMutation.mutate(e.target.value || null)
                  }
                  aria-label="Assign consultant"
                >
                  <option value="">Unassigned</option>
                  {consultantOptions.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setConsultantPickerOpen(false)}
                  className="text-xs text-slate-500 hover:underline"
                >
                  Cancel
                </button>
              </div>
            )}
            {group.contacts.length > 0 ? (
              <ul className="divide-y divide-slate-100 dark:divide-slate-700">
                {group.contacts.map((c, i) => (
                  <ContactRow
                    key={c.id ?? `${group.label}-${i}`}
                    contact={c}
                    canEdit={canEditContacts}
                    onEdit={() => setContactTool('view')}
                  />
                ))}
              </ul>
            ) : (
              (group.fallback ?? (
                <p className="px-4 py-2 text-xs text-slate-400 dark:text-slate-500 italic">
                  None on file.
                </p>
              ))
            )}
          </div>
        ))}
        {canEditContacts && (
          <div className="px-4 pt-3 pb-3">
            <button
              type="button"
              onClick={() => {
                setContactTool('view');
              }}
              className="w-full flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-primary-300 dark:border-primary-700 py-2 text-sm font-medium text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20"
            >
              <Plus className="w-4 h-4" />
              Add contact
            </button>
          </div>
        )}
        <div className="pb-2" />
      </LiteCollapsibleSection>

      {contactTool && (
        <ConfirmProvider>
          <ContactEditModal
            editContactModal={CONTACT_TOOL_MODAL}
            onClose={() => setContactTool(null)}
            siteId={siteId}
            siteContacts={siteContacts}
            organizationId={
              site.organization_id ? String(site.organization_id) : undefined
            }
            mailsEveryone={site.transition_phase !== 'live'}
            initialMode={contactTool}
          />
        </ConfirmProvider>
      )}

      {/* Journal */}
      <LiteCollapsibleSection
        id="journal"
        title="Journal"
        icon={BookOpen}
        open={!!openSections.journal}
        onToggle={() => toggleSection('journal')}
      >
        <JournalSection siteId={siteId} canAddJournal={canAddJournal} />
      </LiteCollapsibleSection>

      {/* Records / reports (read-only) */}
      <LiteCollapsibleSection
        id="records"
        title="Inspections"
        icon={FileText}
        open={!!openSections.records}
        onToggle={() => toggleSection('records')}
      >
        <LiteSiteRecords siteId={siteId} />
      </LiteCollapsibleSection>

      {/* Documents an inspector needs on the ground (Jim 10/7): the BMP
          map — original and updated — and the site notice. */}
      <LiteCollapsibleSection
        id="documents"
        title="Documents"
        icon={FolderOpen}
        open={!!openSections.documents}
        onToggle={() => toggleSection('documents')}
      >
        <LiteSiteDocuments siteId={siteId} />
      </LiteCollapsibleSection>

      {/* 7-day weather (same widget the hub Overview shows, site-scoped) */}
      <LiteCollapsibleSection
        id="weather"
        title="7-Day Weather"
        icon={Cloud}
        open={!!openSections.weather}
        onToggle={() => toggleSection('weather')}
      >
        <div className="p-2">
          <WeatherWidget siteId={siteId} />
        </div>
        <div className="border-t border-slate-100 dark:border-slate-700">
          <WeatherHistoryCalendar siteId={siteId} view="week" />
        </div>
      </LiteCollapsibleSection>

      {/* Open corrective actions — LAST on purpose (Jim 8/9): inspectors
          work these inside the inspection flow; browsing shouldn't lead
          with them. */}
      <LiteCollapsibleSection
        id="actions"
        title="Open Corrective Actions"
        icon={ClipboardCheck}
        open={!!openSections.actions}
        onToggle={() => toggleSection('actions')}
      >
        <OpenActionsSection siteId={siteId} />
      </LiteCollapsibleSection>
    </div>
  );
}
